import CheckboxField from "components/controls/CheckboxField";
import ComboBoxField from "components/controls/ComboBoxField";
import DatePickerField from "components/controls/DatePickerField";
import DoubleNumberInputField from "components/controls/DoubleNumberInputField";
import NumberInputField from "components/controls/NumberInputField";
import TextInputField from "components/controls/TextInputField";
import { vehicleFormClasses } from "pages/vehicle/VehicleForm";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "store/hooks";
import {
  isDisabledExternal,
  isDisabledExternalOrHasBeenActivated,
} from "../../../helpers";
import useInsurerList from "./hooks/useInsurerList";

const FinanceTempHullOnLandFields = ({
  formikProps,
  userRole,
  route,
  vehicle,
  insurerList,
}) => {
  const { t } = useTranslation();
  const { setFieldValue, handleChange, values, errors } = formikProps;
  const taxonomy = useSelector((s) => s.taxonomy);
  const vehicleAppSettings = useSelector((s) => s.vehicle.vehicleAppSettings);

  const paymentMethodOptions = useMemo(() => {
    return taxonomy?.PaymentMethod?.items
      .filter((item) => vehicleAppSettings?.PAYMENT_METHODS.includes(item.code))
      .map((item) => ({
        value: item.id,
        label: t(item.code),
      }));
  }, [t, taxonomy?.PaymentMethod?.items, vehicleAppSettings]);

  const policyStatusOptions = useMemo(() => {
    return taxonomy?.VehiclePolicyStatusCode?.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy?.VehiclePolicyStatusCode?.items]);

  const paymentMeanOptions = useMemo(() => {
    return taxonomy?.PremiumPaymentMean?.items.map((item) => ({
      value: item.id,
      label: t(item.code),
    }));
  }, [t, taxonomy?.PremiumPaymentMean?.items]);

  const insurerData = useInsurerList({
    insurerList,
    values,
    name: "hullOnLandInsurerPartnerNumber",
    subName: "hullOnLandInsurerBranchOffice",
    setFieldValue,
  });

  return (
    <>
      <div className={vehicleFormClasses.item}>
        <CheckboxField
          checked={values?.hullOnLandIsClosingDate}
          errors={errors}
          setFieldValue={setFieldValue}
          name={"hullOnLandIsClosingDate"}
          disabled={false}
          label={"bfm.hullOnLandIsClosingDate.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandIsClosingDate"
          )}
        />
      </div>

      <div className={vehicleFormClasses.item}>
        <CheckboxField
          checked={values?.isFirstForHullOnLandClosingDate}
          errors={errors}
          setFieldValue={setFieldValue}
          name={"isFirstForHullOnLandClosingDate"}
          disabled={false}
          label={"bfm.isFirstForHullOnLandClosingDate.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "isFirstForHullOnLandClosingDate"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.hullOnLandLastStartDate}
          name={"hullOnLandLastStartDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.startDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandLastStartDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.hullOnLandImportEndDate}
          name={"hullOnLandImportEndDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.endDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandImportEndDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.hullOnLandPolicyCancelationDocumentDate}
          name={"hullOnLandPolicyCancelationDocumentDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.cancelationDocumentDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandPolicyCancelationDocumentDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          errors={errors}
          name="hullOnLandInsurerPartnerNumber"
          label={"bfm.fleetForm.hullOnLandInsurerPartnerNumber.label"}
          setFieldValue={setFieldValue}
          options={insurerData.insurerOptions}
          defaultValue={values?.hullOnLandInsurerPartnerNumber}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandInsurerPartnerNumber"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          errors={errors}
          name="hullOnLandInsurerBranchOffice"
          label={"bfm.vehicleForm.insurerBranchOffice.label"}
          setFieldValue={setFieldValue}
          options={insurerData.insurerBranchOfficeOptions}
          defaultValue={values?.hullOnLandInsurerBranchOffice}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandInsurerBranchOffice"
          )}
          confidentiality={null}
        />
      </div>

      <div className={vehicleFormClasses.item}>
        <TextInputField
          value={values.hullOnLandPolicyNumber}
          onChange={handleChange}
          errors={errors}
          name={"hullOnLandPolicyNumber"}
          // multiline
          // rows={4}
          disabled={isDisabledExternal({ vehicleAppSettings, userRole, route })}
          label={"bfm.vehicleForm.policyNumber.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandPolicyNumber"
          )}
        />
      </div>
      {vehicleAppSettings.DEFAULT_COLUMNS.includes("hullOnLandPolicyStatus") ? (
        <div className={vehicleFormClasses.item}>
          <ComboBoxField
            disabled={isDisabledExternal({
              vehicleAppSettings,
              userRole,
              route,
            })}
            errors={errors}
            name="hullOnLandPolicyStatus"
            label={"bfm.vehicleForm.hullOnLandPolicyStatus.label"}
            setFieldValue={setFieldValue}
            options={policyStatusOptions}
            defaultValue={values.hullOnLandPolicyStatus}
            isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
              "hullOnLandPolicyStatus"
            )}
            confidentiality={null}
          />
        </div>
      ) : null}
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternal({ vehicleAppSettings, userRole, route })}
          errors={errors}
          name="hullOnLandPaymentMethod"
          label={"bfm.vehicleForm.paymentMethod.label"}
          setFieldValue={setFieldValue}
          options={paymentMethodOptions}
          defaultValue={values.hullOnLandPaymentMethod}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandPaymentMethod"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <ComboBoxField
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          errors={errors}
          name="hullOnLandPaymentMean"
          label={"bfm.vehicleForm.paymentMean.label"}
          setFieldValue={setFieldValue}
          options={paymentMeanOptions}
          defaultValue={values.hullOnLandPaymentMean}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandPaymentMean"
          )}
          confidentiality={null}
        />
      </div>

      <div className={vehicleFormClasses.item}>
        <DoubleNumberInputField
          setFieldValue={setFieldValue}
          value={values?.hullOnLandAnnualGrossPremium}
          onChange={handleChange}
          errors={errors}
          name={"hullOnLandAnnualGrossPremium"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.annualGrossPremium.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandAnnualGrossPremium"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DoubleNumberInputField
          setFieldValue={setFieldValue}
          value={values?.hullOnLandAnnualNetPremium}
          onChange={handleChange}
          errors={errors}
          name={"hullOnLandAnnualNetPremium"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.annualNetPremium.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandAnnualNetPremium"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.hullOnLandSuspensionDays}
          onChange={handleChange}
          errors={errors}
          name={"hullOnLandSuspensionDays"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.suspensionDays"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandSuspensionDays"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.hullOnLandActivationDays}
          onChange={handleChange}
          errors={errors}
          name={"hullOnLandActivationDays"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.activationDays"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandActivationDays"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.hullOnLandSuspensionDaysLimit}
          onChange={handleChange}
          errors={errors}
          name={"hullOnLandSuspensionDaysLimit"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            userRole,
            route,
            vehicle,
          })}
          label={"bfm.suspensionDaysLimit"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandSuspensionDaysLimit"
          )}
          isNegativeAllowed={true}
        />
      </div>

      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.hullOnLandDueMonth}
          onChange={handleChange}
          errors={errors}
          inputProps={{ type: "number", max: "12" }}
          name={"hullOnLandDueMonth"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.dueMonth.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandDueMonth"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <NumberInputField
          setFieldValue={setFieldValue}
          value={values.hullOnLandDueDay}
          onChange={handleChange}
          errors={errors}
          name={"hullOnLandDueDay"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            route,
            vehicle,
            userRole,
          })}
          label={"bfm.vehicleForm.dueDay.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandDueDay"
          )}
        />
      </div>
      <div className={vehicleFormClasses.item}>
        <DatePickerField
          errors={errors}
          setFieldValue={setFieldValue}
          defaultValue={values.hullOnLandPolicyDocumentDate}
          name={"hullOnLandPolicyDocumentDate"}
          disabled={route === "readOnlyVehicle"}
          label={"bfm.vehicleForm.documentDate.label"}
          placeholder={t("bfm.selectDate")}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandPolicyDocumentDate"
          )}
          confidentiality={null}
        />
      </div>
      <div className={vehicleFormClasses.fullWidthFormItem}>
        <CheckboxField
          checked={values?.hullOnLandIsPolicyIssued}
          errors={errors}
          setFieldValue={setFieldValue}
          name={"hullOnLandIsPolicyIssued"}
          disabled={isDisabledExternalOrHasBeenActivated({
            vehicleAppSettings,
            userRole,
            route,
            vehicle,
          })}
          label={"bfm.isPolicyIssued"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandIsPolicyIssued"
          )}
        />
      </div>
      <div className={vehicleFormClasses.fullWidthFormItem}>
        <TextInputField
          value={values.hullOnLandComment}
          onChange={handleChange}
          errors={errors}
          name={"hullOnLandComment"}
          multiline
          rows={4}
          disabled={isDisabledExternal({ vehicleAppSettings, userRole, route })}
          label={"bfm.fleetForm.fleetComment.label"}
          isMarked={vehicle?.fieldsUpdatedByExternalUser?.includes(
            "hullOnLandComment"
          )}
        />
      </div>
    </>
  );
};

export default FinanceTempHullOnLandFields;
